import "ol/ol.css";
import { Box as MuiBox, styled, Theme, useMediaQuery } from "@mui/material";
import { Map, Overlay } from "ol";
import { LineString, Point, Polygon } from "ol/geom";
import { Draw } from "ol/interaction";
import { DrawEvent } from "ol/interaction/Draw";
import VectorSource from "ol/source/Vector";
import { useContext, useEffect, useRef, useState } from "react";

import { useGetCategories } from "src/api/category";
import { useGetElementsPerMap } from "src/api/element";
import { Enum_Map_Types } from "src/api/map/enum";
import { Map_Z_Index } from "src/const";
import { FormContext } from "src/contexts/form";
import { MapContext } from "src/contexts/map";
import { Enum_Action_Type, Enum_Geometry_Types } from "src/types";
import { Interface_Map_Init_Params, Type_Map_Option } from "src/types/map";
import {
    displayElements,
    getElementOnHover,
    transformCoordinates,
} from "src/utils/element";
import {
    osmMap,
    setOverlayPositionToBottom,
    staticImageMap,
} from "src/utils/map";

import Actions from "./Actions";
import Legend from "./Legend";
import MobileHoverCard from "./mobile/MobileHoverCard";
import MobileSearchBar from "./mobile/MobileSearchBar";
import HoverCard from "./web/HoverCard";

const Box = styled(MuiBox)(() => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
}));

const Container = styled(MuiBox)(() => ({
    display: "flex",
    width: "100%",
    height: "100%",
    position: "relative",
}));

const MapContainer = styled("div")({
    width: "100%",
    position: "relative",
    zIndex: Map_Z_Index,
});

const HIT_TOLERANCE = 12;

// Component used to display and interact with openlayers map
const MapView = () => {
    const [vectorSource] = useState(new VectorSource());
    const {
        selectedMap,
        newElement,
        filters,
        updateCategories,
        updateElements,
        updateNewElement,
        isAuthenticated,
    } = useContext(MapContext);
    const { type, actionType, handleForm, openForm } = useContext(FormContext);

    const [mapInstance, setMapInstance] = useState<Map | null>(null);
    const [zoom, setZoom] = useState<number>(20);
    const overlayRef = useRef(undefined);
    const [overlayContent, setOverlayContent] = useState<React.ReactNode>(null);

    const { data: categories, isLoading: AreCategoriesLoading } =
        useGetCategories() || [];
    const { data: elements, isLoading: AreElementsLoading } =
        useGetElementsPerMap(selectedMap?.id, filters) || [];

    // used to detect the click, used on hover event
    const [isCLicked, setIsClicked] = useState(false);
    const isClickedRef = useRef(isCLicked);

    // used to keep the drawing pointer active when hovering on existing elements
    const [isDrawing, setIsDrawing] = useState(false);
    const isDrawingRef = useRef(isDrawing);

    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md"),
    );

    useEffect(() => {
        isClickedRef.current = isCLicked;
    }, [isCLicked]);

    const mapOptions: Type_Map_Option[] = [
        {
            type: Enum_Map_Types.STANDARD_OSM,
            function: osmMap,
        },
        {
            type: Enum_Map_Types.STATIC_IMAGE,
            function: staticImageMap,
        },
    ];

    useEffect(() => {
        isDrawingRef.current = isDrawing;
    }, [isDrawing]);

    useEffect(() => {
        if (selectedMap) {
            const option =
                mapOptions.find((option) => option.type === selectedMap.type) ||
                mapOptions[0];
            const param: Interface_Map_Init_Params = {
                data: selectedMap,
                target: "map",
                vectorSource,
            };
            const map: Map = option.function(param);
            setMapInstance(map);

            const overlay: Overlay = new Overlay({
                element: overlayRef.current,
                stopEvent: true,
                insertFirst: true,
            });
            map.addOverlay(overlay);

            if (!isDrawing) {
                const handleClick = (event: any) => {
                    if (isMobile) {
                        const feature = map.forEachFeatureAtPixel(
                            event.pixel,
                            (f) => f,
                            { hitTolerance: HIT_TOLERANCE },
                        );
                        if (feature && elements) {
                            const element = getElementOnHover({
                                feature,
                                elements,
                            });
                            if (element) {
                                updateNewElement(element);
                                setOverlayContent(
                                    <MobileHoverCard
                                        setOverlayContent={() =>
                                            setOverlayContent(null)
                                        }
                                    />,
                                );
                                setOverlayPositionToBottom(overlay, map);
                            }
                        }
                    } else {
                        setOverlayContent(null);
                        setIsClicked(false);
                    }
                };

                const handlePointerMove = (event: any) => {
                    if (isDrawingRef.current) return;

                    if (!isClickedRef.current) {
                        const feature = map.forEachFeatureAtPixel(
                            event.pixel,
                            (f) => f,
                            { hitTolerance: HIT_TOLERANCE },
                        );
                        if (feature && elements) {
                            const element = getElementOnHover({
                                feature,
                                elements,
                            });
                            if (element) {
                                updateNewElement(element);
                                setOverlayContent(<HoverCard />);
                                overlay.setPosition(event.coordinate);
                            }
                        }
                    }
                };

                map.on("click", handleClick);
                map.on("pointermove", handlePointerMove);

                return () => {
                    map.un("click", handleClick);
                    map.un("pointermove", handlePointerMove);

                    map.setTarget(undefined);
                };
            }
        }
    }, [selectedMap, elements]);

    useEffect(() => {
        mapInstance &&
            mapInstance.getView().on("change:resolution", () => {
                setZoom(mapInstance.getView().getResolution() || 1);
            });

        if (
            !newElement &&
            selectedMap &&
            mapInstance &&
            type &&
            actionType === Enum_Action_Type.create
        ) {
            const draw = new Draw({
                source: vectorSource,
                type,
            });
            setIsDrawing(true);
            mapInstance.addInteraction(draw);

            const onDrawEnd = async (event: DrawEvent) => {
                const geometry = event.feature.getGeometry();
                if (geometry) {
                    const coordinates = (
                        geometry as Point | LineString | Polygon
                    ).getCoordinates();
                    const geometryType =
                        geometry.getType() as Enum_Geometry_Types;

                    const transformedCoordinates = transformCoordinates(
                        geometryType,
                        coordinates,
                    );

                    updateNewElement({
                        ...newElement!,
                        coordinates: {
                            type: geometryType,
                            coordinates: transformedCoordinates,
                        },
                    });

                    const zoomLevel =
                        mapInstance.getView().getZoom() || selectedMap.minZoom;
                    const center = mapInstance.getView().getCenter();
                    if (
                        zoomLevel !==
                        parseFloat(sessionStorage.getItem("currentZoom")!)
                    ) {
                        sessionStorage.setItem(
                            "currentZoom",
                            zoomLevel?.toString(),
                        );
                    }

                    if (
                        center !==
                        JSON.parse(sessionStorage.getItem("currentCenter")!)
                    ) {
                        sessionStorage.setItem(
                            "currentCenter",
                            JSON.stringify(center),
                        );
                    }

                    setIsDrawing(false);
                    mapInstance.removeInteraction(draw);
                }
                handleForm(true);
            };

            draw.on("drawend", onDrawEnd);

            return () => {
                draw.un("drawend", onDrawEnd);
                mapInstance.removeInteraction(draw);
            };
        }
    }, [mapInstance, type, newElement]);

    useEffect(() => {
        if (!AreCategoriesLoading) {
            updateCategories(categories);
        }
    }, [AreCategoriesLoading]);

    useEffect(() => {
        if (!openForm && categories && elements) {
            vectorSource.refresh();
            displayElements({ zoom, elements, categories, vectorSource });
        }
    }, [zoom, openForm]);

    useEffect(() => {
        if (!AreElementsLoading && !AreCategoriesLoading) {
            updateElements(elements);
            displayElements({ zoom, elements, categories, vectorSource });
        }
    }, [
        zoom,
        vectorSource,
        AreElementsLoading,
        elements,
        AreCategoriesLoading,
    ]);

    return (
        <Box>
            <Container>
                <MapContainer id="map" />

                <Box onClick={() => setIsClicked(!isCLicked)} ref={overlayRef}>
                    {overlayContent}
                </Box>

                <Legend />

                {isMobile && <MobileSearchBar />}

                {isAuthenticated && <Actions />}
            </Container>
        </Box>
    );
};

export default MapView;
